import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useSite } from "./SiteContext";

// Shared metadata owner. CMS settings are the source of truth for business facts.
export default function Seo({
  title, description, image, imageAlt, path, canonical, type = "website",
  noIndex = false, schema = null, breadcrumbs = [], record = null,
}) {
  noIndex = Boolean(noIndex || record?.no_index);
  canonical = canonical || record?.canonical;
  const { settings } = useSite();
  const { pathname } = useLocation();
  const origin = window.location.origin;
  const absolute = (value, base = origin) => {
    if (!value) return undefined;
    try {
      const url = new URL(value, base);
      return /^https?:$/.test(url.protocol) && !url.username && !url.password ? url.href : undefined;
    } catch { return undefined; }
  };
  const site = (absolute(settings.site_url || process.env.REACT_APP_SITE_URL) || origin).replace(/\/$/, "");
  const cleanUrl = (value) => {
    const resolved = absolute(value, `${site}/`);
    if (!resolved) return undefined;
    const url = new URL(resolved);
    url.search = "";
    url.hash = "";
    url.pathname = url.pathname.replace(/\/+$/, "") || "/";
    return url.href;
  };
  const pageUrl = cleanUrl(path === undefined ? pathname : path || "/");
  const url = cleanUrl(canonical) || pageUrl;
  const fullTitle = title || settings.default_meta_title || "Azulo Travel | Bespoke Luxury Journeys";
  const summary = String(description || settings.default_meta_description || "Private journeys in India and worldwide, tailored to your interests by Azulo Travel.")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/[*#]/g, "").replace(/\s+/g, " ").trim();
  const socialImage = absolute(image || settings.default_og_image || settings.hero_poster || "/azulo-logo.webp", `${site}/`);
  const agencyId = `${site}/#agency`;
  const websiteId = `${site}/#website`;
  const graphs = [];

  if (!noIndex) {
    graphs.push({
      "@type": "TravelAgency", "@id": agencyId,
      name: settings.brand_name || "Azulo Travel", url: `${site}/`,
      logo: absolute("/azulo-logo.webp", `${site}/`),
      image: absolute(settings.default_og_image, `${site}/`),
      description: settings.organisation_schema_note || summary,
      parentOrganization: settings.parent_company ? { "@type": "Organization", name: settings.parent_company } : undefined,
      address: settings.address || undefined,
      telephone: settings.phone || undefined,
      email: settings.email || undefined,
      slogan: settings.tagline || undefined,
      areaServed: { "@type": "Country", name: "India" },
      sameAs: [settings.instagram, settings.facebook, settings.linkedin].map((v) => absolute(v)).filter(Boolean),
    });
    graphs.push({ "@type": "WebSite", "@id": websiteId, url: `${site}/`, name: settings.brand_name || "Azulo Travel", inLanguage: "en-IN", publisher: { "@id": agencyId } });
    graphs.push({
      "@type": "WebPage", "@id": `${pageUrl}#webpage`, url: pageUrl, name: fullTitle,
      description: summary, inLanguage: "en-IN", isPartOf: { "@id": websiteId },
      primaryImageOfPage: { "@type": "ImageObject", url: socialImage },
    });
    if (breadcrumbs.length) {
      graphs.push({
        "@type": "BreadcrumbList", "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem", position: i + 1, name: b.label,
          item: b.to ? cleanUrl(b.to) : pageUrl,
        })),
      });
    }
    if (schema) {
      for (const entry of (Array.isArray(schema) ? schema : [schema])) {
        const { "@context": context, ...node } = entry;
        graphs.push({
          ...node, "@id": node["@id"] || `${pageUrl}#main`, url: pageUrl,
          image: node.image ? absolute(node.image, `${site}/`) : undefined,
          ...(node["@type"] === "Article" ? {
            mainEntityOfPage: { "@id": `${pageUrl}#webpage` },
            publisher: { "@id": agencyId }, inLanguage: "en-IN",
          } : {}),
          ...(["Service", "TouristTrip"].includes(node["@type"]) ? { provider: { "@id": agencyId } } : {}),
        });
      }
    }
  }

  return (
    <Helmet>
      <html lang="en-IN" />
      <title>{fullTitle}</title>
      <meta name="description" content={summary} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noIndex ? "noindex, follow" : "index, follow, max-image-preview:large"} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:site_name" content={settings.brand_name || "Azulo Travel"} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={summary} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={socialImage} />
      <meta property="og:image:alt" content={imageAlt || title || "Azulo Travel"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={summary} />
      <meta name="twitter:image" content={socialImage} />
      <meta name="twitter:image:alt" content={imageAlt || title || "Azulo Travel"} />
      {graphs.length > 0 && <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@graph": graphs }).replace(/</g, "\\u003c")}</script>}
    </Helmet>
  );
}
